import { useEffect, useState } from 'react';
import { JournalEntryForm } from './JournalEntryForm';
import { TrialBalance } from './TrialBalance';
import { ARReceiptForm } from './ARReceiptForm';
import { ARReceiptRegister } from './ARReceiptRegister';
import { APPaymentForm } from './APPaymentForm';
import { PDCRegister } from './PDCRegister';
import { CustomerStatement } from './CustomerStatement';
import { SupplierStatement } from './SupplierStatement';
import { FiscalPeriods } from './FiscalPeriods';
import { ChartOfAccounts } from './ChartOfAccounts';
import { Tabs } from '../../components/ui/tabs';
import { FlowEntryBanner } from '../../components/workflow/FlowEntryBanner';
import { PostingCorrections } from './PostingCorrections';
import { useAuth } from '../../context/AuthContext';

import { PageHelp } from '../../components/PageHelp';
import { useSearchParams } from 'react-router-dom';

export function FinancialsDashboard() {
    const { hasPermission } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab') || 'overview';
    const [activeTab, setActiveTab] = useState(tabFromUrl);

    const tabs = [
        { id: 'overview', label: 'Overview (TB)' },
        { id: 'journal', label: 'New Journal' },
        { id: 'ar', label: 'Customer Receipt (AR)' },
        { id: 'ar-register', label: 'Receipt Register' },
        { id: 'ap', label: 'Supplier Payment (AP)' },
        { id: 'pdc', label: 'PDC Register' },
        { id: 'coa', label: 'Chart of Accounts' },
        { id: 'periods', label: 'Period Controls' },
        { id: 'customer-statement', label: 'Customer Statement' },
        { id: 'supplier-statement', label: 'Supplier Statement' },
        ...(hasPermission('finance.corrections.view') ? [{ id: 'corrections', label: 'Posting Corrections' }] : [])
    ];

    useEffect(() => {
        if (!tabs.find(tab => tab.id === tabFromUrl)) {
            const next = new URLSearchParams(searchParams);
            next.set('tab', 'overview');
            setSearchParams(next, { replace: true });
            if (activeTab !== 'overview') setActiveTab('overview');
            return;
        }
        if (tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [tabFromUrl, activeTab, searchParams, setSearchParams]);

    const handleTabChange = (nextTab: string) => {
        setActiveTab(nextTab);
        const next = new URLSearchParams(searchParams);
        next.set('tab', nextTab);
        setSearchParams(next, { replace: true });
    };

    return (
        <div className="space-y-6">
            <div>
                <div className="flex items-center gap-2">
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Financials</h1>
                    <PageHelp
                        title="About Financials Module"
                        description="Your real-time General Ledger for managing the company's financial health."
                    >
                        <div className="space-y-4">
                            <section>
                                <h3 className="font-semibold text-foreground mb-1">1. Business Logic</h3>
                                <p>This module acts as your <strong>General Ledger (GL)</strong>. It aggregates all financial transactions from Sales, Purchasing, and Inventory in <strong>real-time</strong>. Balances are updated instantly upon saving transactions.</p>
                            </section>

                            <section>
                                <h3 className="font-semibold text-foreground mb-1">2. Key Features</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li><strong>Overview (Trial Balance):</strong> Displays live balances for all accounts. Use this to check cash flow and total sales.</li>
                                    <li><strong>New Journal:</strong> For manual transactions like Payroll, Depreciation, or corrections. Requires Debits to equal Credits.</li>
                                    <li><strong>Customer Receipt (AR):</strong> Streamlined form for receiving customer payments (Cashes/Bank Dr, AR Cr).</li>
                                    <li><strong>Supplier Payment (AP):</strong> Streamlined form for paying suppliers (AP Dr, Cash/Bank Cr).</li>
                                    <li><strong>Period Controls:</strong> Open, close, or lock fiscal periods before month-end and year-end review.</li>
                                </ul>
                            </section>

                            <section>
                                <h3 className="font-semibold text-foreground mb-1">3. Technical Context</h3>
                                <p>Data flows automatically from other modules (e.g., Invoices post to Sales/AR). Manual entries marked as <code>IsPosted=0</code> are still treated as live/effective immediately.</p>
                            </section>
                        </div>
                    </PageHelp>
                </div>
                <p className="text-muted-foreground">General Ledger, Accounts Receivable & Payable</p>
            </div>

            <div className="space-y-4">
                <FlowEntryBanner
                    title="Finance Flow Coach"
                    pathLabel="AR + AP Lifecycle"
                    nextHint="Post invoices and supplier bills first, then reconcile receipts/payments for closure."
                    helpModule="finance"
                />
                <Tabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />

                <div className="mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                    {activeTab === 'overview' && <TrialBalance />}
                    {activeTab === 'journal' && <JournalEntryForm />}
                    {activeTab === 'ar' && <ARReceiptForm />}
                    {activeTab === 'ar-register' && <ARReceiptRegister />}
                    {activeTab === 'ap' && <APPaymentForm />}
                    {activeTab === 'pdc' && <PDCRegister />}
                    {activeTab === 'coa' && <ChartOfAccounts />}
                    {activeTab === 'periods' && <FiscalPeriods />}
                    {activeTab === 'customer-statement' && <CustomerStatement />}
                    {activeTab === 'supplier-statement' && <SupplierStatement />}
                    {activeTab === 'corrections' && <PostingCorrections />}
                </div>
            </div>
        </div>
    );
}
